import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Building2,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { useAuth } from '@context/AuthContext';
import { useUIStore } from '@/store/ui.store';
import { entradasVisibles, cuantasSeOcultan, GRUPOS_DE_MENU, MENU_ANCHO } from '@/utils/navegacion';
import { FS, PASO, useTema } from '@components/M02/casaTema';

const ROL_LABELS: Record<string, string> = {
  ceo: 'CEO', cfo: 'CFO',
  gerente_proyecto: 'Gerente de Proyecto',
  gerente_ventas: 'Gerente de Ventas',
  gerente_postventa: 'Gerente Post-venta',
  vendedor: 'Vendedor',
  auxiliar_comercial: 'Auxiliar Comercial',
  auxiliar_administrativo: 'Auxiliar Administrativo',
  auxiliar_compras: 'Auxiliar Compras',
  arquitecto: 'Arquitecto',
  contabilidad_cxp: 'Contabilidad CxP',
  administrador: 'Administrador',
  cobranza: 'Cobranza',
  legal: 'Legal',
};

const SIDEBAR_EXPANDED  = MENU_ANCHO.abierto;
const SIDEBAR_COLLAPSED = MENU_ANCHO.colapsado;

// ─── Tooltip colapsado ────────────────────────────────────────
const Tooltip: React.FC<{ label: string }> = ({ label }) => {
  const T = useTema();
  return (
  <div
    className="absolute left-full ml-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
               whitespace-nowrap pointer-events-none z-[60]
               opacity-0 group-hover:opacity-100 transition-opacity duration-150"
    style={{
      backgroundColor: T.fg,
      color: T.panelSolid,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      top: '50%',
      transform: 'translateY(-50%)',
    }}
  >
    {label}
    <span
      className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent"
      style={{ borderRightColor: T.fg }}
    />
  </div>
  );
};

// ─── Sidebar ─────────────────────────────────────────────────
const Sidebar: React.FC = () => {
  const { usuario, logout } = useAuth();
  const location = useLocation();
  const { sidebarAbierto, toggleSidebar } = useUIStore();
  const T = useTema();
  const w = sidebarAbierto ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  // I-251: las entradas salen de la lista ÚNICA (`utils/navegacion.ts`) y la regla de acceso es la
  // misma de la ruta (`tieneAccesoAModulo`, dentro de `veLaEntrada`): el menú refleja, no decide.
  const sesion = { nivel: usuario?.nivel ?? 0, modulos: usuario?.modulos ?? [], accesos: usuario?.accesos };
  const visibles = entradasVisibles(sesion);
  const ocultas = cuantasSeOcultan(sesion);

  const iniciales = usuario?.nombre
    ? usuario.nombre.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : 'RM';
  const rolLabel = usuario?.rol ? (ROL_LABELS[usuario.rol] ?? usuario.rol) : '';

  return (
    <aside
      className="fixed z-50 flex flex-col overflow-hidden"
      style={{
        top: 12, bottom: 12, left: 12,
        width: w,
        borderRadius: 18,
        background: T.panel,
        backdropFilter: 'blur(28px) saturate(160%)',
        WebkitBackdropFilter: 'blur(28px) saturate(160%)',
        border: `1px solid ${T.line}`,
        boxShadow: T.shadow,
        transition: 'width 220ms cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {/* ── Logo ─────────────────────────────────────────────── */}
      <div
        className="flex items-center flex-shrink-0 overflow-hidden"
        style={{
          height: 64,
          borderBottom: `1px solid ${T.lineSoft}`,
          padding: sidebarAbierto ? '0 14px' : '0',
          justifyContent: sidebarAbierto ? 'flex-start' : 'center',
          gap: 10,
          transition: 'padding 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Ícono */}
        <div
          className="flex items-center justify-center rounded-xl flex-shrink-0"
          style={{ width: 34, height: 34, background: T.accentSoft, border: `1px solid ${T.accentLine}` }}
        >
          <Building2 size={17} color={T.accent} strokeWidth={2.5} />
        </div>

        {/* Texto */}
        <div
          style={{
            maxWidth: sidebarAbierto ? 160 : 0,
            opacity: sidebarAbierto ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-width 220ms cubic-bezier(0.4,0,0.2,1), opacity 160ms ease',
            flexShrink: 0,
          }}
        >
          <p className="font-bold whitespace-nowrap" style={{ color: T.fg, fontSize: FS.marcaNombre, lineHeight: 1.2 }}>
            RM Desarrollos
          </p>
          <p className="whitespace-nowrap font-medium" style={{ color: T.fg3, fontSize: PASO.s10_5 }}>
            ERP Inmobiliario
          </p>
        </div>
      </div>

      {/* ── Navegación ──────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden" style={{ padding: '6px 0' }}>
        {GRUPOS_DE_MENU.map((grupo) => {
          const sectionItems = visibles.filter((i) => i.grupo === grupo);
          if (sectionItems.length === 0) return null;
          const section = { label: grupo };

          return (
            <div key={section.label} style={{ marginBottom: 2 }}>
              {/* Etiqueta de sección */}
              {sidebarAbierto && (
                <p
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: T.fg3,
                    fontSize: FS.menuGrupo,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase' as const,
                    padding: '10px 14px 3px',
                  }}
                >
                  {section.label}
                </p>
              )}
              {!sidebarAbierto && (
                <div style={{ height: 8 }} />
              )}

              {/* Items */}
              {sectionItems.map((item) => {
                const Icon = item.icono;
                const to = item.path;
                const isActive =
                  item.path === '/dashboard' ? location.pathname === '/dashboard'
                  : location.pathname.startsWith(to);

                return (
                  <div key={item.id} className="relative group" style={{ padding: '1px 7px' }}>
                    <NavLink
                      to={to}
                      className="flex items-center rounded-lg transition-all duration-150"
                      style={{
                        height: 34,
                        gap: sidebarAbierto ? 9 : 0,
                        paddingLeft: sidebarAbierto ? 9 : 0,
                        justifyContent: sidebarAbierto ? 'flex-start' : 'center',
                        background: isActive ? T.accentSoft : 'transparent',
                        boxShadow: isActive ? '0 1px 2px rgba(15,20,25,0.06), 0 4px 12px -4px rgba(15,20,25,0.08)' : 'none',
                        border: `1px solid ${isActive ? T.accentLine : 'transparent'}`,
                        color: isActive ? T.fg : T.fg2,
                        opacity: isActive ? 1 : 0.75,
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          (e.currentTarget as HTMLElement).style.background = T.chip;
                          (e.currentTarget as HTMLElement).style.color = T.fg2;
                          (e.currentTarget as HTMLElement).style.opacity = '1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                          (e.currentTarget as HTMLElement).style.color = T.fg2;
                          (e.currentTarget as HTMLElement).style.opacity = '0.75';
                        }
                      }}
                    >
                      <Icon
                        size={14}
                        strokeWidth={isActive ? 2.5 : 2}
                        className="flex-shrink-0"
                        style={{ color: isActive ? T.accent : 'inherit' }}
                      />
                      <span
                        className="font-medium whitespace-nowrap overflow-hidden"
                        style={{
                          fontSize: FS.menuItem,
                          maxWidth: sidebarAbierto ? 180 : 0,
                          opacity: sidebarAbierto ? 1 : 0,
                          transition: 'max-width 220ms cubic-bezier(0.4,0,0.2,1), opacity 150ms ease',
                          flex: 1,
                          color: 'inherit',
                          fontWeight: isActive ? 600 : 400,
                        }}
                      >
                        {item.label}
                      </span>
                      {item.marca === 'mudaAlProyecto' && sidebarAbierto && (
                        <span
                          title="Esta pantalla se muda a cada proyecto"
                          aria-label="se muda al proyecto"
                          className="flex-shrink-0 rounded-full"
                          style={{ width: 6, height: 6, marginRight: 8, backgroundColor: T.warn }}
                        />
                      )}
                    </NavLink>

                    {!sidebarAbierto && <Tooltip label={item.label} />}
                  </div>
                );
              })}
            </div>
          );
        })}
        {ocultas > 0 && sidebarAbierto && (
          <p
            data-testid="entradas-ocultas"
            style={{
              margin: '8px 9px 4px', padding: '9px 11px', borderRadius: 10,
              border: `1px dashed ${T.line}`, color: T.fg3, fontSize: PASO.s11, lineHeight: 1.4,
            }}
          >
            {ocultas === 1 ? '1 entrada no se dibuja' : `${ocultas} entradas no se dibujan`} para esta persona:
            no tiene ese acceso. No se ven apagadas — no existen.
          </p>
        )}
      </nav>

      {/* ── Footer ──────────────────────────────────────────── */}
      <div
        className="flex-shrink-0"
        style={{ borderTop: `1px solid ${T.lineSoft}`, padding: '8px 7px' }}
      >
        {/* Usuario — emerald-tinted card */}
        <div
          className="flex items-center rounded-xl overflow-hidden mb-2"
          style={{
            padding: sidebarAbierto ? '8px 10px' : '8px 0',
            gap: sidebarAbierto ? 9 : 0,
            justifyContent: sidebarAbierto ? 'flex-start' : 'center',
            background: T.accentSoft,
            border: `1px solid ${T.accentLine}`,
          }}
        >
          <div className="relative group flex-shrink-0">
            <div
              className="flex items-center justify-center rounded-full text-xs font-bold"
              style={{
                width: 28, height: 28,
                background: T.chip, color: T.fg2,
                fontSize: PASO.s10_5,
              }}
            >
              {iniciales}
            </div>
            {!sidebarAbierto && <Tooltip label={usuario?.nombre ?? ''} />}
          </div>

          <div
            style={{
              maxWidth: sidebarAbierto ? 160 : 0,
              opacity: sidebarAbierto ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-width 220ms cubic-bezier(0.4,0,0.2,1), opacity 150ms ease',
            }}
          >
            <p className="font-semibold truncate whitespace-nowrap" style={{ color: T.fg, fontSize: PASO.s11_5 }}>
              {usuario?.nombre ?? 'Usuario'}
            </p>
            <p className="whitespace-nowrap truncate" style={{ color: T.fg2, fontSize: PASO.s10 }}>
              {rolLabel}
            </p>
          </div>
        </div>

        {/* Logout */}
        <div className="relative group">
          <button
            onClick={logout}
            className="flex items-center rounded-lg w-full transition-all duration-150"
            style={{
              height: 32,
              gap: sidebarAbierto ? 8 : 0,
              paddingLeft: sidebarAbierto ? 9 : 0,
              justifyContent: sidebarAbierto ? 'flex-start' : 'center',
              color: T.fg3,
              fontSize: PASO.s12,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = T.dangerSoft;
              (e.currentTarget as HTMLElement).style.color = T.danger;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = '';
              (e.currentTarget as HTMLElement).style.color = T.fg3;
            }}
          >
            <LogOut size={13} className="flex-shrink-0" />
            <span
              className="font-medium whitespace-nowrap overflow-hidden"
              style={{
                maxWidth: sidebarAbierto ? 140 : 0,
                opacity: sidebarAbierto ? 1 : 0,
                transition: 'max-width 220ms cubic-bezier(0.4,0,0.2,1), opacity 150ms ease',
              }}
            >
              Cerrar sesión
            </span>
          </button>
          {!sidebarAbierto && <Tooltip label="Cerrar sesión" />}
        </div>

        {/* Toggle */}
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center rounded-lg w-full mt-1 transition-all duration-150"
          style={{ height: 26, color: T.fg3 }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = T.chip;
            (e.currentTarget as HTMLElement).style.color = T.fg2;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = '';
            (e.currentTarget as HTMLElement).style.color = T.fg3;
          }}
          title={sidebarAbierto ? 'Colapsar' : 'Expandir'}
        >
          {sidebarAbierto
            ? <ChevronLeft size={13} strokeWidth={2} />
            : <ChevronRight size={13} strokeWidth={2} />
          }
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
